import { queryOptions } from "@tanstack/react-query";
import { API_BASE_URL, api, authedFetch } from "./client";
import { payload, payloadOrNullOn404 } from "./envelope";
import { ApiError, errorCode, errorMessage } from "./errors";
import type { components } from "./schema";

export type Establishment = components["schemas"]["Establishment"];
export type UpdateEstablishmentRequest =
  components["schemas"]["UpdateEstablishmentRequest"];
export type PaymentMethod = components["schemas"]["PaymentMethod"];
export type PaymentMethodCode = components["schemas"]["PaymentMethodCode"];
export type BusinessHours = components["schemas"]["BusinessHours"];
export type BusinessHoursRequest = components["schemas"]["BusinessHoursRequest"];
export type UpdateBusinessHoursRequest =
  components["schemas"]["UpdateBusinessHoursRequest"];

export function establishmentQuery(establishmentId: string) {
  return queryOptions({
    queryKey: ["establishment", establishmentId] as const,
    queryFn: () =>
      payload(
        api.GET("/establishments/{id}", {
          params: { path: { id: establishmentId } },
        }),
      ),
  });
}

/**
 * Catálogo de formas de pagamento.
 *
 * Vem da API, e não de uma constante aqui, porque o rótulo em português é o
 * mesmo texto que o bot fala no WhatsApp: com uma cópia no painel, os dois
 * poderiam divergir sem ninguém notar. `staleTime: Infinity` porque é lista
 * fechada — só muda quando o servidor sobe com uma linha nova.
 */
export function paymentMethodsQuery() {
  return queryOptions({
    queryKey: ["payment-methods"] as const,
    queryFn: () => payload(api.GET("/payment-methods")),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * Update parcial: só mande o que a tela edita — campo ausente preserva o
 * valor atual. `timezone` fica de fora de propósito: trocá-lo com compromisso
 * futuro é bloqueado pela API (`409 timezone_change_blocked`) e não há UI pra
 * isso aqui.
 */
export function updateEstablishment(
  establishmentId: string,
  body: UpdateEstablishmentRequest,
): Promise<Establishment> {
  return payload(
    api.PUT("/establishments/{id}", {
      params: { path: { id: establishmentId } },
      body,
    }),
  );
}

/**
 * Intervalos de expediente DO ESTABELECIMENTO. Cada linha é um intervalo
 * (pausa de almoço = duas linhas no mesmo `day_of_week`); a tela de
 * configurações edita um intervalo por dia — linhas extras de um dia são
 * preservadas pelo sync, só não aparecem.
 */
export function businessHoursQuery(establishmentId: string) {
  return queryOptions({
    queryKey: ["establishment", establishmentId, "business-hours"] as const,
    queryFn: () =>
      payload(
        api.GET("/establishments/{id}/business-hours", {
          params: { path: { id: establishmentId } },
        }),
      ),
  });
}

export function createBusinessHours(
  establishmentId: string,
  body: BusinessHoursRequest,
): Promise<BusinessHours> {
  return payload(
    api.POST("/establishments/{id}/business-hours", {
      params: { path: { id: establishmentId } },
      body,
    }),
  );
}

export function updateBusinessHours(
  id: string,
  body: UpdateBusinessHoursRequest,
): Promise<BusinessHours> {
  return payload(api.PUT("/business-hours/{id}", { params: { path: { id } }, body }));
}

/**
 * DELETE devolve 204 sem corpo, então não passa pelo `payload` (que exige
 * `data`) — aqui só interessa saber se veio erro.
 */
export async function deleteBusinessHours(id: string): Promise<void> {
  const { error } = await api.DELETE("/business-hours/{id}", {
    params: { path: { id } },
  });
  if (error) {
    throw new ApiError(errorCode(error) ?? "unknown_error", errorMessage(error));
  }
}

/**
 * Metadados da logo do salão — `null` para quem nunca subiu nenhuma, que é
 * o estado da maioria.
 *
 * A imagem em si NÃO passa por aqui: ela é servida por uma rota pública
 * (`logoImageUrl`), consumida direto por um `<img>`. Trazer bytes para
 * dentro do React só para exibi-los desperdiçaria o cache do navegador,
 * que é justamente o que faz essa imagem custar quase nada.
 */
export function logoQuery(establishmentId: string) {
  return queryOptions({
    queryKey: ["establishment", establishmentId, "logo"] as const,
    queryFn: () =>
      payloadOrNullOn404(
        api.GET("/establishments/{id}/logo", {
          params: { path: { id: establishmentId } },
        }),
      ),
  });
}

/**
 * Endereço público da imagem.
 *
 * O `v` é o carimbo da última troca, e existe por um motivo prático: a
 * resposta vem com `Cache-Control` de 5 minutos, então sem ele o dono
 * trocava a logo e continuava vendo a antiga — concluindo, com razão, que
 * o upload não funcionou. Com o carimbo, a URL muda junto com a imagem e o
 * navegador busca a nova na hora.
 */
export function logoImageUrl(establishmentId: string, updatedAt?: string): string {
  const base = `${API_BASE_URL}/public/establishments/${establishmentId}/logo`;
  return updatedAt ? `${base}?v=${encodeURIComponent(updatedAt)}` : base;
}

/**
 * Envia a logo.
 *
 * Vai por `fetch` direto, e não pelo cliente tipado: o corpo é
 * `multipart/form-data` com um arquivo, e o openapi-fetch serializa corpo
 * como JSON. O `Content-Type` é omitido de PROPÓSITO — quem precisa
 * escrevê-lo é o navegador, que acrescenta o `boundary` do multipart;
 * defini-lo à mão produz um corpo que o servidor não consegue separar.
 */
export async function uploadLogo(establishmentId: string, file: File): Promise<void> {
  const form = new FormData();
  form.append("logo", file);

  const response = await authedFetch(
    `${API_BASE_URL}/establishments/${establishmentId}/logo`,
    { method: "PUT", body: form },
  );

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const err = (body as { error?: unknown } | null)?.error;
    throw new ApiError(
      errorCode(err) ?? "upload_failed",
      errorMessage(err) ?? "Não foi possível enviar a imagem.",
    );
  }
}

export async function deleteLogo(establishmentId: string): Promise<void> {
  const response = await authedFetch(
    `${API_BASE_URL}/establishments/${establishmentId}/logo`,
    { method: "DELETE" },
  );
  if (!response.ok) {
    throw new ApiError("delete_failed", "Não foi possível remover a imagem.");
  }
}
